"use client"

import type { Slug } from "@/modules/jobSearchPrefiltered/types"
import type { FunctionComponent, MouseEventHandler } from "react"

import { clsx } from "clsx"
import { useState } from "react"

import { Button } from "@jobteaser/spark/components/Button"
import { Heading } from "@jobteaser/spark/components/Heading"

import { MAX_DISPLAYED_NETLINKING_TAGS } from "@/modules/jobSearchPrefiltered/constants"
import { useLocale } from "@/modules/locales/useLocale"
import { useTrackGenericClickEvent } from "@/modules/tracking/useTrackGenericClickEvent"

import { Tag } from "./Tag"

import styles from "./NetlinkingBlockItem.module.css"

type NetlinkingBlockProps = {
  className?: string
  netlinkingBlockLabel: string
  availableSlugs: (Slug & { titleTranslation: string })[]
  seeMoreLabel: string
}

export const NetlinkingBlockItem: FunctionComponent<NetlinkingBlockProps> = ({
  className,
  netlinkingBlockLabel,
  availableSlugs,
  seeMoreLabel,
}) => {
  const locale = useLocale()
  const [isExpanded, setIsExpanded] = useState(availableSlugs.length <= MAX_DISPLAYED_NETLINKING_TAGS)
  const trackEvent = useTrackGenericClickEvent()

  const displayedSlugs = isExpanded ? availableSlugs : availableSlugs.slice(0, MAX_DISPLAYED_NETLINKING_TAGS)

  const handleDisplayMore: MouseEventHandler<HTMLButtonElement> = (event): void => {
    event.stopPropagation()
    trackEvent("prefiltered-jobsearches-netlinking-block-see-more")
    setIsExpanded(true)
  }

  return (
    <section className={clsx(styles.netlinkingBlock, className)} data-testid="PrefilteredJobSearch-NetlinkingBlock">
      <Heading as="h2" className={styles.title} variant="title3">
        {netlinkingBlockLabel}
      </Heading>

      <div className={styles.tags}>
        {displayedSlugs.map(slug => {
          return <Tag slug={slug} key={slug.slug} label={slug.titleTranslation} locale={locale} />
        })}
      </div>

      {!isExpanded && (
        <Button
          variant="tertiary"
          size="small"
          onClick={handleDisplayMore}
          className={styles.seeMore}
          data-testid="PrefilteredJobSearch-NetlinkingBlock__seeMore"
        >
          {seeMoreLabel}
        </Button>
      )}
    </section>
  )
}
